'use client'
import * as React from 'react'
import { Button } from '@repo/design-system-cf/components/button'
import { useUserQuery } from 'auth/hooks'
import { isMobileUserAgent } from '@repo/web-utils/is-mobile-user-agent'
import { IconAdd } from '@repo/design-system-cf/components/icons'
import { useHasAllAccessQuery } from '@repo/cf-user/hooks/use-user-data'
import { AvatarTextInput } from './avatar-text-input'
import { SkeletonItem } from 'components/skeleton-item'
import { useCreatePostProjectDialog } from '../create-post-project-dialog/create-post-project-dialog-context'

// oxlint-disable-next-line complexity
export function PostProjectInputSection({ slug }: { slug: string }) {
  const { data: userHasAllAccess, isPending: isLoadingHasAllAccess } = useHasAllAccessQuery()
  const { data: user, isPending: isLoadingUser } = useUserQuery()
  const { openDialog } = useCreatePostProjectDialog()
  const isMobileDevice = isMobileUserAgent()
  const [text, setText] = React.useState('')

  if (isLoadingUser || isLoadingHasAllAccess) {
    return <PostProjectInputSectionSkeleton />
  }

  if (!userHasAllAccess && user?.me?.user?.slug !== slug) {
    return null
  }

  const userName = user?.me?.user?.firstName || 'User'

  const placeholderText = isMobileDevice
    ? 'Tell us about your project'
    : `What was your most recent project, ${userName}?`

  return userHasAllAccess && user?.me?.user?.slug === slug ? (
    <div className="sm:rounded-[12px] sm:border sm:border-[#1313151F] sm:p-4 sm:bg-cf-purple-50 lg:bg-cf-grey-100 w-full mb-4">
      <div className="flex sm:gap-4 gap-3 flex-col sm:flex-row" onClick={openDialog}>
        <AvatarTextInput
          placeholder={placeholderText}
          avatarSrc={user?.me?.user?.avatarUrl || undefined}
          value={text}
          onChange={e => setText(e.target.value)}
          onClear={() => setText('')}
        />
        {isMobileDevice ? (
          <Button
            data-testid="post-project-button-mobile"
            className="w-full"
            iconLeft={<IconAdd />}
          >
            Add a new project
          </Button>
        ) : (
          <Button data-testid="post-project-button" size="large">
            Post
          </Button>
        )}
      </div>
    </div>
  ) : null
}

export function PostProjectInputSectionSkeleton() {
  return (
    <div className="sm:rounded-[12px] sm:border sm:border-[#1313151F] sm:p-4 sm:bg-cf-purple-50 lg:bg-cf-grey-100 w-full lg:max-w-[756px] mx-auto mb-4">
      <div className="flex sm:gap-4 gap-3 flex-col sm:flex-row">
        <div className="flex items-center gap-3 flex-1">
          <SkeletonItem className="rounded-full w-10 h-10 shrink-0" />

          <SkeletonItem className="h-[44px] w-full rounded-[12px]" />
        </div>

        <div className="flex sm:w-auto w-full">
          <SkeletonItem className="h-[44px] w-full sm:w-[140px] rounded-[999px]" />
        </div>
      </div>
    </div>
  )
}
